if (!customElements.get('c-kitbuilder')) {
  class CKitbuilder extends HTMLElement {
    connectedCallback() {
      this.destroyed = false;
      this.busy = false;
      this.timers = [];
      this.sectionId = this.dataset.sectionId || '';
      this.layout = this.dataset.layout === 'all' ? 'all' : 'steps';
      this.requireAll = this.dataset.requireAll !== 'false';
      this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
      this.maxQty = Math.min(10, Math.max(1, parseInt(this.dataset.maxQty, 10) || 5));
      this.offerMsg = this.dataset.offerMessage || '';
      this.unlockedMsg = this.dataset.unlockedMessage || '';
      const type = this.dataset.offerType;
      this.offer = {
        code: (this.dataset.offerCode || '').trim(),
        type: type === 'percent' || type === 'fixed' || type === 'free' ? type : 'none',
        value: parseFloat(this.dataset.offerValue) || 0,
        minQty: parseInt(this.dataset.offerMinQty, 10) || 0,
        minAmount: parseInt(this.dataset.offerMinAmount, 10) || 0,
        label: (this.dataset.offerLabel || '').trim()
      };
      this.storageKey = 'c-kitbuilder:' + this.sectionId;
      this.root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      this.reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

      this.stepsNav = this.querySelector('.c-kitbuilder__steps');
      this.steps = [];
      this.querySelectorAll('.c-kitbuilder__panel').forEach((panel, i) => {
        const cards = {};
        panel.querySelectorAll('.c-kitbuilder__card[data-variant-id]').forEach((btn) => {
          const id = btn.dataset.variantId;
          if (id && !cards[id]) {
            cards[id] = {
              id: id,
              price: parseInt(btn.dataset.price, 10) || 0,
              name: btn.dataset.name || '',
              sub: btn.dataset.sub || '',
              image: btn.dataset.image || '',
              available: btn.dataset.available !== 'false'
            };
          }
        });
        const max = Math.max(1, parseInt(panel.dataset.max, 10) || 1);
        const min = Math.min(max, Math.max(0, parseInt(panel.dataset.min, 10) || 0));
        this.steps.push({
          index: i,
          id: panel.dataset.stepId || String(i),
          title: panel.dataset.title || '',
          min: min,
          max: max,
          allowQty: panel.dataset.allowQty !== 'false',
          panel: panel,
          cards: cards,
          pill: this.querySelector('.c-kitbuilder__pill[data-step-index="' + i + '"]'),
          counterEl: panel.querySelector('.c-kitbuilder__counter-n'),
          msgEl: panel.querySelector('.c-kitbuilder__msg'),
          nextBtn: panel.querySelector('.c-kitbuilder__next')
        });
      });
      this.picks = this.steps.map(() => []);
      this.qty = this.steps.map(() => ({}));
      this.advanceTimer = null;
      this.current = 0;

      this.summary = this.querySelector('.c-kitbuilder__summary');
      this.picksEl = this.querySelector('.c-kitbuilder__picks');
      this.toggleBtn = this.querySelector('.c-kitbuilder__toggle');
      this.toggleText = this.querySelector('.c-kitbuilder__toggle-text');
      this.toggleCount = this.querySelector('.c-kitbuilder__toggle-count');
      this.submitBtn = this.querySelector('.c-kitbuilder__submit');
      this.statusEl = this.querySelector('.c-kitbuilder__status');
      this.errorEl = this.querySelector('.c-kitbuilder__error');
      this.totalEl = this.querySelector('.c-kitbuilder__total');
      this.wasEl = this.querySelector('.c-kitbuilder__was');
      this.saveEl = this.querySelector('.c-kitbuilder__save');
      this.barFill = this.querySelector('.c-kitbuilder__bar-fill');
      this.progressText = this.querySelector('.c-kitbuilder__progress-text');

      this.onClick = (e) => this.handleClick(e);
      this.onKeydown = (e) => {
        if (e.key === 'Escape' && this.summary && this.summary.getAttribute('data-expanded') === 'true') {
          this.setExpanded(false);
          if (this.toggleBtn) this.toggleBtn.focus();
        }
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          entries.forEach((en) => this.classList.toggle('is-inview', en.isIntersecting));
        }, { threshold: 0 });
        this.io.observe(this);
      } else {
        this.classList.add('is-inview');
      }

      this.restore();
      this.current = this.firstIncomplete();
      this.showCurrent(false);
      this.render();
    }

    disconnectedCallback() {
      this.destroyed = true;
      if (this.onClick) this.removeEventListener('click', this.onClick);
      if (this.onKeydown) this.removeEventListener('keydown', this.onKeydown);
      if (this.io) this.io.disconnect();
      if (this.onOutside) document.removeEventListener('pointerdown', this.onOutside, true);
      this.timers.forEach((t) => clearTimeout(t));
      this.timers = [];
    }

    later(fn, ms) {
      const t = setTimeout(() => {
        this.timers = this.timers.filter((x) => x !== t);
        if (!this.destroyed) fn();
      }, ms);
      this.timers.push(t);
      return t;
    }

    clearAdvance() {
      if (this.advanceTimer) {
        clearTimeout(this.advanceTimer);
        this.timers = this.timers.filter((x) => x !== this.advanceTimer);
        this.advanceTimer = null;
      }
    }

    getQty(si, id) {
      const step = this.steps[si];
      if (!step || !step.allowQty) return 1;
      return Math.min(this.maxQty, Math.max(1, this.qty[si][id] || 1));
    }

    resetPicks() {
      this.picks = this.steps.map(() => []);
      this.qty = this.steps.map(() => ({}));
    }

    isDone(step) {
      const n = this.picks[step.index].length;
      return n > 0 && n >= step.min;
    }

    isSatisfied(step) {
      return this.picks[step.index].length >= step.min;
    }

    firstIncomplete() {
      for (let i = 0; i < this.steps.length; i++) {
        if (!this.isDone(this.steps[i]) && this.steps[i].min > 0) return i;
      }
      return Math.max(0, this.steps.length - 1);
    }

    restore() {
      try {
        const raw = window.sessionStorage.getItem(this.storageKey);
        if (!raw) return;
        const data = JSON.parse(raw);
        if (!data || typeof data !== 'object') return;
        this.steps.forEach((step) => {
          const ids = data[step.id];
          if (!Array.isArray(ids)) return;
          ids.forEach((id) => {
            const key = String(id);
            const list = this.picks[step.index];
            if (step.cards[key] && step.cards[key].available && list.length < step.max && list.indexOf(key) === -1) {
              list.push(key);
            }
          });
        });
        const q = data.__q;
        if (q && typeof q === 'object') {
          this.steps.forEach((step) => {
            const map = q[step.id];
            if (!map || typeof map !== 'object' || !step.allowQty) return;
            this.picks[step.index].forEach((id) => {
              const n = parseInt(map[id], 10);
              if (n > 1) this.qty[step.index][id] = Math.min(this.maxQty, n);
            });
          });
        }
      } catch (err) { /* storage unavailable */ }
    }

    persist() {
      try {
        const data = {};
        const q = {};
        this.steps.forEach((step) => {
          data[step.id] = this.picks[step.index];
          q[step.id] = this.qty[step.index];
        });
        data.__q = q;
        window.sessionStorage.setItem(this.storageKey, JSON.stringify(data));
      } catch (err) { /* storage unavailable */ }
    }

    handleClick(e) {
      const target = e.target;
      if (!target || !target.closest) return;
      const qbtn = target.closest('.c-kitbuilder__qty-btn');
      if (qbtn && this.contains(qbtn)) {
        const qpanel = qbtn.closest('.c-kitbuilder__panel');
        const si = qbtn.dataset.step !== undefined ? parseInt(qbtn.dataset.step, 10) : (qpanel ? parseInt(qpanel.dataset.stepIndex, 10) : NaN);
        this.changeQty(si, qbtn.dataset.id, parseInt(qbtn.dataset.dir, 10) || 0, qbtn);
        return;
      }
      const card = target.closest('.c-kitbuilder__card');
      if (card && this.contains(card)) {
        const panel = card.closest('.c-kitbuilder__panel');
        if (panel) this.toggleCard(parseInt(panel.dataset.stepIndex, 10), card.dataset.variantId, card);
        return;
      }
      const btn = target.closest('button');
      if (!btn || !this.contains(btn)) return;
      if (btn.classList.contains('c-kitbuilder__pill')) {
        this.goTo(parseInt(btn.dataset.stepIndex, 10), true);
      } else if (btn.classList.contains('c-kitbuilder__next')) {
        this.goTo(this.current + 1, true);
      } else if (btn.classList.contains('c-kitbuilder__back')) {
        this.goTo(this.current - 1, true);
      } else if (btn.classList.contains('c-kitbuilder__remove')) {
        this.removePick(parseInt(btn.dataset.step, 10), btn.dataset.id);
      } else if (btn.classList.contains('c-kitbuilder__clear')) {
        this.resetPicks();
        this.setError('');
        this.setStatus('Kit cleared');
        this.persist();
        this.current = 0;
        this.showCurrent(false);
        this.render();
      } else if (btn.classList.contains('c-kitbuilder__toggle')) {
        this.setExpanded(this.summary.getAttribute('data-expanded') !== 'true');
      } else if (btn.classList.contains('c-kitbuilder__close')) {
        this.setExpanded(false);
        if (this.toggleBtn) this.toggleBtn.focus();
      } else if (btn.classList.contains('c-kitbuilder__submit')) {
        this.submit();
      }
    }

    setExpanded(open) {
      if (!this.summary) return;
      this.summary.setAttribute('data-expanded', open ? 'true' : 'false');
      if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (this.toggleText) this.toggleText.textContent = open ? 'Hide kit' : 'View kit';
      if (open && !this.onOutside) {
        // Close the mobile sheet on a tap outside it.
        this.onOutside = (e) => {
          if (this.summary.getAttribute('data-expanded') === 'true' && !this.summary.contains(e.target)) this.setExpanded(false);
        };
        document.addEventListener('pointerdown', this.onOutside, true);
      }
    }

    setStatus(msg) { if (this.statusEl) this.statusEl.textContent = msg; }
    setError(msg) { if (this.errorEl) this.errorEl.textContent = msg; }

    toggleCard(si, id, cardEl) {
      const step = this.steps[si];
      if (!step || this.busy) return;
      const info = step.cards[id];
      if (!info || !info.available) return;
      const list = this.picks[si];
      const at = list.indexOf(id);
      let added = false;
      if (step.msgEl) step.msgEl.textContent = '';
      this.setError('');
      this.clearAdvance();
      if (at > -1) {
        list.splice(at, 1);
        delete this.qty[si][id];
        this.setStatus('Removed ' + info.name);
      } else if (step.max === 1) {
        this.picks[si] = [id];
        this.qty[si] = {};
        added = true;
        this.setStatus('Selected ' + info.name);
      } else if (list.length >= step.max) {
        if (step.msgEl) step.msgEl.textContent = 'You can pick up to ' + step.max + ' in this step. Remove one to choose another.';
        return;
      } else {
        list.push(id);
        added = true;
        this.setStatus('Added ' + info.name + '. ' + list.length + ' of ' + step.max + ' picked.');
      }
      if (added && cardEl && !this.reduced) {
        cardEl.classList.remove('is-pop');
        void cardEl.offsetWidth;
        cardEl.classList.add('is-pop');
        const done = () => { cardEl.classList.remove('is-pop'); cardEl.removeEventListener('animationend', done); };
        cardEl.addEventListener('animationend', done);
      }
      this.persist();
      this.render();
      if (added && this.layout === 'steps' && si === this.current && this.picks[si].length >= step.max && si < this.steps.length - 1) {
        this.advanceTimer = this.later(() => {
          this.advanceTimer = null;
          if (this.current === si) this.goTo(si + 1, true);
        }, step.allowQty ? 900 : (this.reduced ? 0 : 450));
      }
    }

    changeQty(si, id, dir, srcBtn) {
      const step = this.steps[si];
      if (!step || this.busy || !step.allowQty || !dir) return;
      const list = this.picks[si];
      if (list.indexOf(id) < 0) return;
      const info = step.cards[id];
      if (!info) return;
      const label = info.sub ? info.sub + ' ' + info.name : info.name;
      const next = this.getQty(si, id) + dir;
      this.clearAdvance();
      this.setError('');
      if (step.msgEl) step.msgEl.textContent = '';
      if (next < 1) {
        const card = step.panel.querySelector('.c-kitbuilder__card[data-variant-id="' + id + '"]');
        this.removePick(si, id, card);
        return;
      }
      if (next > this.maxQty) {
        this.setStatus('Maximum of ' + this.maxQty + ' per item');
        return;
      }
      this.qty[si][id] = next;
      this.setStatus('Quantity of ' + label + ': ' + next);
      this.persist();
      const inSummary = !!(srcBtn && this.picksEl && this.picksEl.contains(srcBtn));
      this.render();
      if (inSummary && this.picksEl) {
        const again = this.picksEl.querySelector('.c-kitbuilder__qty-btn[data-step="' + si + '"][data-id="' + id + '"][data-dir="' + dir + '"]');
        if (again) again.focus();
      }
    }

    preselectVariant(variantId) {
      const id = String(variantId || '');
      if (!id || this.destroyed) return false;
      let applied = false;
      for (let i = 0; i < this.steps.length; i++) {
        const step = this.steps[i];
        const info = step.cards[id];
        if (!info) continue;
        const list = this.picks[i];
        if (info.available && list.indexOf(id) === -1 && list.length < step.max && !(step.max === 1 && list.length)) {
          list.push(id);
          applied = true;
        }
        const card = step.panel.querySelector('.c-kitbuilder__card[data-variant-id="' + id + '"]');
        const item = card ? card.closest('.c-kitbuilder__item.is-extra') : null;
        if (item) {
          step.panel.querySelectorAll('.c-kitbuilder__item.is-extra').forEach((li) => { li.hidden = false; });
          const wrap = step.panel.querySelector('.c-kitbuilder__more-wrap');
          if (wrap) wrap.remove();
        }
        break;
      }
      if (applied) this.persist();
      this.current = 0;
      this.showCurrent(false);
      this.render();
      return applied;
    }

    removePick(si, id, focusEl) {
      const list = this.picks[si];
      if (!list || this.busy) return;
      const at = list.indexOf(String(id));
      if (at < 0) return;
      const info = this.steps[si].cards[String(id)];
      list.splice(at, 1);
      delete this.qty[si][String(id)];
      this.setStatus('Removed ' + (info ? info.name : 'item'));
      this.persist();
      this.render();
      const target = focusEl || this.picksEl;
      if (target) target.focus();
    }

    goTo(i, scroll) {
      if (isNaN(i) || i < 0 || i >= this.steps.length) return;
      this.current = i;
      this.showCurrent(true);
      this.render();
      const step = this.steps[i];
      if (!scroll) return;
      const behavior = this.reduced ? 'auto' : 'smooth';
      try {
        if (this.layout === 'all') {
          step.panel.scrollIntoView({ behavior: behavior, block: 'start' });
        } else if (this.stepsNav) {
          const top = this.stepsNav.getBoundingClientRect().top;
          if (top < 60 || top > window.innerHeight * 0.6) {
            this.stepsNav.scrollIntoView({ behavior: behavior, block: 'start' });
          }
        }
      } catch (err) { /* ignore */ }
    }

    showCurrent(animate) {
      this.steps.forEach((step) => {
        const on = step.index === this.current;
        step.panel.classList.toggle('is-current', on);
        if (this.layout === 'steps') {
          const wasHidden = step.panel.hidden;
          step.panel.hidden = !on;
          if (on && wasHidden && animate && !this.reduced) {
            step.panel.classList.remove('is-entering');
            void step.panel.offsetWidth;
            step.panel.classList.add('is-entering');
            const panel = step.panel;
            const done = () => { panel.classList.remove('is-entering'); panel.removeEventListener('animationend', done); };
            panel.addEventListener('animationend', done);
          }
        }
      });
      const cur = this.steps[this.current];
      if (cur && cur.pill && this.stepsNav && this.stepsNav.scrollTo) {
        const rail = this.stepsNav;
        const left = cur.pill.parentNode.offsetLeft - (rail.clientWidth - cur.pill.offsetWidth) / 2;
        try { rail.scrollTo({ left: Math.max(0, left), behavior: this.reduced || !animate ? 'auto' : 'smooth' }); } catch (err) { /* ignore */ }
      }
    }

    units() {
      const list = [];
      this.steps.forEach((step) => {
        this.picks[step.index].forEach((id) => {
          const info = step.cards[id];
          if (!info) return;
          const n = this.getQty(step.index, id);
          for (let k = 0; k < n; k++) list.push({ step: step, info: info });
        });
      });
      return list;
    }

    saving(prices, total) {
      const o = this.offer;
      const qty = prices.length;
      if (o.type === 'none' || qty === 0) return 0;
      if (o.minQty && qty < o.minQty) return 0;
      if (o.minAmount && total < o.minAmount) return 0;
      if (o.type === 'percent') return Math.round(total * o.value / 100);
      if (o.type === 'fixed') return Math.min(total, Math.round(o.value * 100));
      const n = Math.max(0, Math.round(o.value));
      return prices.slice().sort((a, b) => a - b).slice(0, n).reduce((s, v) => s + v, 0);
    }

    isValid() {
      if (this.busy) return false;
      const total = this.units().length;
      if (total < 1) return false;
      if (this.requireAll) return this.steps.every((s) => this.isSatisfied(s));
      return true;
    }

    shortReward() {
      const o = this.offer;
      if (o.type === 'percent') return o.value + '% off';
      if (o.type === 'fixed') return this.formatMoney(Math.round(o.value * 100)) + ' off';
      if (o.type === 'free') return Math.round(o.value) + (Math.round(o.value) === 1 ? ' item' : ' items') + ' free';
      return o.label;
    }

    render() {
      if (this.destroyed) return;
      const units = this.units();
      const prices = units.map((u) => u.info.price);
      const total = prices.reduce((s, v) => s + v, 0);
      const save = this.saving(prices, total);
      const qty = units.length;

      this.steps.forEach((step) => {
        const list = this.picks[step.index];
        const n = list.length;
        const done = this.isDone(step);
        step.panel.classList.toggle('is-complete', done);
        if (step.counterEl) step.counterEl.textContent = String(n);
        step.panel.querySelectorAll('.c-kitbuilder__card').forEach((btn) => {
          const on = list.indexOf(btn.dataset.variantId) > -1;
          btn.classList.toggle('is-selected', on);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
          const item = btn.closest('.c-kitbuilder__item');
          const qel = item ? item.querySelector('.c-kitbuilder__qty') : null;
          if (item) item.classList.toggle('has-qty', on && !!qel);
          if (qel) {
            qel.hidden = !on;
            const q = on ? this.getQty(step.index, btn.dataset.variantId) : 1;
            const nEl = qel.querySelector('.c-kitbuilder__qty-n');
            if (nEl) nEl.textContent = String(q);
            const plus = qel.querySelector('[data-dir="1"]');
            if (plus) plus.setAttribute('aria-disabled', q >= this.maxQty ? 'true' : 'false');
          }
        });
        if (step.nextBtn) step.nextBtn.disabled = !this.isSatisfied(step);
        if (step.pill) {
          const isCur = step.index === this.current;
          step.pill.classList.toggle('is-current', isCur);
          step.pill.classList.toggle('is-done', done);
          if (isCur) step.pill.setAttribute('aria-current', 'step');
          else step.pill.removeAttribute('aria-current');
          const meta = step.pill.querySelector('.c-kitbuilder__pill-meta');
          if (meta) meta.textContent = n > 0 ? n + ' picked' : (step.min > 0 ? 'Required' : 'Optional');
        }
      });

      this.renderPicks();

      if (this.toggleCount) this.toggleCount.textContent = qty + (qty === 1 ? ' item' : ' items');
      if (this.totalEl) this.totalEl.textContent = this.formatMoney(Math.max(0, total - save));
      if (this.wasEl) {
        const show = qty > 0 && save > 0;
        this.wasEl.hidden = !show;
        this.wasEl.textContent = show ? this.formatMoney(total) : '';
      }
      if (this.saveEl) {
        const show = qty > 0 && save > 0;
        this.saveEl.hidden = !show;
        this.saveEl.textContent = show ? 'You save ' + this.formatMoney(save) : '';
      }
      this.renderProgress(qty, total);
      if (this.submitBtn) this.submitBtn.disabled = !this.isValid();
    }

    renderProgress(qty, total) {
      const o = this.offer;
      if (!o.code || !this.progressText) return;
      let text = '';
      let pct = 0;
      if (o.type === 'none') {
        const done = qty > 0 && this.steps.every((s) => this.isSatisfied(s));
        const complete = this.steps.filter((s) => this.isSatisfied(s)).length;
        pct = Math.round((complete / this.steps.length) * 100);
        text = done ? this.unlockedMsg : this.offerMsg;
      } else {
        const remQty = Math.max(0, o.minQty - qty);
        const remAmt = Math.max(0, o.minAmount - total);
        const unlocked = qty > 0 && remQty === 0 && remAmt === 0;
        const ratios = [];
        if (o.minQty) ratios.push(qty / o.minQty);
        if (o.minAmount) ratios.push(total / o.minAmount);
        pct = unlocked ? 100 : Math.round(Math.min.apply(null, ratios.length ? ratios : [0]) * 100);
        if (unlocked) {
          text = this.unlockedMsg + (o.label ? ': ' + o.label : '');
        } else if (qty === 0) {
          text = this.offerMsg;
        } else if (remQty > 0) {
          text = 'Add ' + remQty + ' more ' + (remQty === 1 ? 'item' : 'items') + ' to unlock ' + this.shortReward();
        } else {
          text = 'Spend ' + this.formatMoney(remAmt) + ' more to unlock ' + this.shortReward();
        }
      }
      if (this.barFill) this.barFill.style.width = Math.max(0, Math.min(100, pct)) + '%';
      this.progressText.textContent = text;
      this.progressText.classList.toggle('is-unlocked', pct >= 100);
    }

    renderPicks() {
      if (!this.picksEl) return;
      const frag = document.createDocumentFragment();
      this.steps.forEach((step) => {
        const group = document.createElement('div');
        group.className = 'c-kitbuilder__group';
        const h = document.createElement('h4');
        h.className = 'c-kitbuilder__group-title';
        h.textContent = step.title;
        group.appendChild(h);
        const list = this.picks[step.index];
        if (!list.length) {
          const p = document.createElement('p');
          p.className = 'c-kitbuilder__none';
          p.textContent = step.min > 0 ? 'Not chosen yet' : 'Optional, skip if you like';
          group.appendChild(p);
        }
        list.forEach((id) => {
          const info = step.cards[id];
          if (!info) return;
          const row = document.createElement('div');
          row.className = 'c-kitbuilder__row';
          if (info.image) {
            const img = document.createElement('img');
            img.className = 'c-kitbuilder__thumb';
            img.src = info.image;
            img.alt = '';
            img.width = 48;
            img.height = 48;
            img.loading = 'lazy';
            row.appendChild(img);
          }
          const text = document.createElement('div');
          text.className = 'c-kitbuilder__row-text';
          const name = document.createElement('span');
          name.className = 'c-kitbuilder__row-name';
          name.textContent = info.name;
          text.appendChild(name);
          if (info.sub) {
            const sub = document.createElement('span');
            sub.className = 'c-kitbuilder__row-sub';
            sub.textContent = info.sub;
            text.appendChild(sub);
          }
          const price = document.createElement('span');
          price.className = 'c-kitbuilder__row-price';
          const q = this.getQty(step.index, id);
          price.textContent = this.formatMoney(info.price * q) + (q > 1 ? ' (' + this.formatMoney(info.price) + ' each)' : '');
          text.appendChild(price);
          if (step.allowQty) text.appendChild(this.buildQty(step, id, info, q));
          row.appendChild(text);
          const rm = document.createElement('button');
          rm.type = 'button';
          rm.className = 'c-kitbuilder__remove';
          rm.dataset.step = String(step.index);
          rm.dataset.id = id;
          rm.setAttribute('aria-label', 'Remove ' + info.name);
          rm.textContent = 'Remove';
          row.appendChild(rm);
          group.appendChild(row);
        });
        frag.appendChild(group);
      });
      this.picksEl.textContent = '';
      this.picksEl.appendChild(frag);
    }

    buildQty(step, id, info, q) {
      const label = info.sub ? info.sub + ' ' + info.name : info.name;
      const wrap = document.createElement('span');
      wrap.className = 'c-kitbuilder__qty';
      wrap.setAttribute('role', 'group');
      wrap.setAttribute('aria-label', 'Quantity of ' + label);
      const mk = (dir, text, d) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'c-kitbuilder__qty-btn';
        b.dataset.step = String(step.index);
        b.dataset.id = id;
        b.dataset.dir = String(dir);
        b.setAttribute('aria-label', text + ' quantity of ' + label);
        if (dir > 0 && q >= this.maxQty) b.setAttribute('aria-disabled', 'true');
        b.innerHTML = '<svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="' + d + '"/></svg>';
        return b;
      };
      const n = document.createElement('span');
      n.className = 'c-kitbuilder__qty-n';
      n.textContent = String(q);
      wrap.appendChild(mk(-1, 'Decrease', 'M5 10h10'));
      wrap.appendChild(n);
      wrap.appendChild(mk(1, 'Increase', 'M5 10h10M10 5v10'));
      return wrap;
    }

    async submit() {
      if (this.busy || !this.isValid()) return;
      this.busy = true;
      this.setError('');
      if (this.submitBtn) {
        this.submitBtn.disabled = true;
        this.submitBtn.setAttribute('aria-busy', 'true');
        if (window.CVayveCartFx && window.CVayveCartFx.early) window.CVayveCartFx.early(this.submitBtn);
      }
      try {
        const agg = {};
        const order = [];
        this.units().forEach((u) => {
          const id = u.info.id;
          if (!agg[id]) { agg[id] = 0; order.push(id); }
          agg[id] += 1;
        });
        const props = { _kit: this.sectionId };
        if (this.offer.code) {
          props._vayve_offer = this.offer.code;
          props._vayve_group = 'kit-' + Date.now().toString(36);
        }
        const items = order.map((id) => ({ id: Number(id), quantity: agg[id], properties: props }));
        const res = await fetch(this.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        });
        let data = {};
        try { data = await res.json(); } catch (err) { data = {}; }
        if (!res.ok || data.status) {
          this.setError(data.description || data.message || 'Could not add the kit to your cart. Please try again.');
          return;
        }
        this.resetPicks();
        this.persist();
        this.current = 0;
        this.showCurrent(false);
        this.setExpanded(false);
        this.setStatus('Kit added to your cart');
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
        if (!document.querySelector('m-cart-drawer')) {
          window.location.href = this.root + 'cart';
        }
      } catch (err) {
        this.setError('Something went wrong. Please try again.');
      } finally {
        this.busy = false;
        if (this.submitBtn) this.submitBtn.removeAttribute('aria-busy');
        this.render();
      }
    }

    formatMoney(cents) {
      const format = this.moneyFormat;
      const value = Math.round(Number(cents) || 0);
      const fmt = (number, precision, thousands, decimal) => {
        thousands = thousands || ',';
        decimal = decimal || '.';
        const fixed = (number / 100).toFixed(precision);
        const parts = fixed.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const rest = parts[1] ? decimal + parts[1] : '';
        return dollars + rest;
      };
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (m, name) => {
        switch (name) {
          case 'amount': return fmt(value, 2);
          case 'amount_no_decimals': return fmt(value, 0);
          case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
          case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
          case 'amount_no_decimals_with_space_separator': return fmt(value, 0, ' ', ',');
          case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
          default: return fmt(value, 2);
        }
      });
    }
  }
  customElements.define('c-kitbuilder', CKitbuilder);
}

// Show more: reveal remaining choices in a step; also reveal any restored selection hidden behind it.
document.addEventListener('click', function (e) {
  var btn = e.target && e.target.closest ? e.target.closest('.c-kitbuilder [data-more]') : null;
  if (!btn) return;
  var panel = btn.closest('.c-kitbuilder__panel') || btn.parentElement.parentElement;
  panel.querySelectorAll('.c-kitbuilder__item.is-extra').forEach(function (li) { li.hidden = false; });
  btn.parentElement.remove();
});
window.addEventListener('load', function () {
  document.querySelectorAll('.c-kitbuilder__item.is-extra [aria-pressed="true"]').forEach(function (card) {
    var panel = card.closest('.c-kitbuilder__panel');
    if (!panel) return;
    panel.querySelectorAll('.c-kitbuilder__item.is-extra').forEach(function (li) { li.hidden = false; });
    var w = panel.querySelector('.c-kitbuilder__more-wrap'); if (w) w.remove();
  });
});


/* Step search: filters the cards of one step (its products and collection) only. */
(function () {
  if (window.cKitStepSearch) return;
  window.cKitStepSearch = true;

  function apply(input) {
    var panel = input.closest('.c-kitbuilder__panel');
    if (!panel) return;
    var q = String(input.value || '').trim().toLowerCase();
    var words = q ? q.split(/\s+/) : [];
    var clear = panel.querySelector('[data-step-search-clear]');
    var empty = panel.querySelector('[data-step-search-empty]');
    var status = panel.querySelector('[data-step-search-status]');
    var moreWrap = panel.querySelector('.c-kitbuilder__more-wrap');
    if (clear) clear.hidden = !q;
    var shown = 0;
    panel.querySelectorAll('.c-kitbuilder__item').forEach(function (li) {
      if (!q) {
        li.hidden = !!(moreWrap && li.classList.contains('is-extra'));
        return;
      }
      var card = li.querySelector('.c-kitbuilder__card');
      var text = ((card && card.dataset.name) || '') + ' ' + ((card && card.dataset.sub) || '') + ' ' + li.textContent;
      text = text.toLowerCase();
      var match = words.every(function (w) { return text.indexOf(w) !== -1; });
      li.hidden = !match;
      if (match) shown += 1;
    });
    if (moreWrap) moreWrap.hidden = !!q;
    if (empty) empty.hidden = !q || shown > 0;
    if (status) status.textContent = q ? shown + (shown === 1 ? ' product found' : ' products found') : '';
  }

  var timer = null;
  document.addEventListener('input', function (e) {
    var input = e.target && e.target.closest ? e.target.closest('.c-kitbuilder [data-step-search]') : null;
    if (!input) return;
    clearTimeout(timer);
    timer = setTimeout(function () { apply(input); }, 120);
  });
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('.c-kitbuilder [data-step-search-clear]') : null;
    if (!btn) return;
    var input = btn.closest('.c-kitbuilder__search').querySelector('[data-step-search]');
    input.value = '';
    apply(input);
    input.focus();
  });
  // Enter must not submit anything or jump steps.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' && e.target && e.target.matches && e.target.matches('.c-kitbuilder [data-step-search]')) e.preventDefault();
  });
})();
